import { useQuery } from '@tanstack/react-query';

import type { GetApproveCounterSignRobosignTarPaperworkResponse } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { useQueryErrorBoundary } from '@common/hooks/useErrorBoundary';
import { apiClient } from '@config/server/apiClient';
import { ordersEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get approve counter-sign robosign tar paperwork data
 */
export const getApproveCounterSignRobosignTarPaperwork = async (
	servicesProviderId: string,
	orderId: string,
): Promise<GetApproveCounterSignRobosignTarPaperworkResponse> => {
	const response = await apiClient.get<GetApproveCounterSignRobosignTarPaperworkResponse>(
		ordersEndpoints.approveCounterSignRobosignTarPaperwork(servicesProviderId, orderId),
	);
	return response.data;
};

/**
 * Hook for getting approve counter-sign robosign tar paperwork data
 * Returns buyer info, counter-sign forms list, and POA forms list
 * @param orderId - The purchased vehicle ID (orderId in frontend terms)
 */
export const useGetApproveCounterSignRobosignTarPaperwork = (orderId: string | undefined) => {
	const errorBoundaryOptions = useQueryErrorBoundary();
	const servicesProviderId = useCurrentServicesProviderId();

	return useQuery({
		queryKey: queries.orders.detail(servicesProviderId, orderId || '')._ctx.progress._ctx
			.approveCounterSignRobosignTarPaperwork.queryKey,
		queryFn: () => {
			if (!orderId) {
				throw new Error('Unable to get approve counter-sign robosign tar paperwork data without an order ID.');
			}
			return getApproveCounterSignRobosignTarPaperwork(servicesProviderId, orderId);
		},
		enabled: Boolean(orderId) && Boolean(servicesProviderId),
		staleTime: Infinity,
		...errorBoundaryOptions,
	});
};

